<template>
  <component
    :is="isFlagged || isProcessing ? 'div' : RouterLink"
    :to="isFlagged || isProcessing ? undefined : rowLink"
    class="relative block w-full pr-5 py-2.5"
    :class="isProcessing ? 'cursor-default' : 'cursor-pointer'"
    @click="handleRowClick"
  >
    <div class="absolute right-0 bottom-0 left-[34px] h-px bg-[#2d2d2d]/8" />

    <div class="flex h-5 items-center justify-between pl-[34px]">
      <CreatingReport v-if="isProcessing" />

      <div
        v-else-if="isGroup"
        class="text-[14px] leading-[18px] font-medium text-gray-800"
      >
        {{ formatNumber(infringement.copyrights.length) }} reports
      </div>

      <span v-else-if="coverOnly" class="text-[14px] font-medium text-green-700"
        >Accepted cover match</span
      >

      <div
        v-else-if="isReport && infringement.matches?.score != null"
        class="flex items-center gap-x-2"
      >
        <div class="text-[14px] leading-[18px] font-medium" :class="accentText">
          {{ formatNumber(score) }}% match
        </div>
        <div class="w-[100px]">
          <Progress
            :percentage="score"
            size="sm"
            :color="levelColor"
            track-color="var(--color-gray-200)"
          />
        </div>
      </div>

      <span
        v-else
        class="inline-flex w-fit items-center rounded-[4px] px-[6px] py-[4px] text-[12px] leading-[12px]"
        :class="accentBg"
      >
        {{ levelLabel }}
      </span>

      <div class="flex items-center">
        <Pill
          variant="default"
          :icon="labelIcon"
          :class="{ 'text-gray-800!': isReportLabel }"
        >
          {{ isReportLabel ? 'Report' : 'Flagged' }}
        </Pill>

        <div
          v-if="isGroup"
          class="ml-[13px] flex size-[18px] shrink-0 items-center justify-center"
        >
          <ChevronRightIcon class="size-4 text-gray-800" />
        </div>

        <button
          v-else
          type="button"
          class="ml-[13px] flex size-[18px] shrink-0 items-center justify-center"
          :class="{ invisible: isProcessing }"
          @click.stop.prevent="handleMenu"
        >
          <MenuIcon class="size-[18px] text-gray-800" />
        </button>
      </div>
    </div>

    <div class="relative mt-[3px] flex flex-col gap-y-2.5">
      <SortIcon
        class="absolute top-1/2 left-[34px] z-10 size-3.5 -translate-y-1/2 text-gray-800"
      />

      <div class="flex items-center gap-x-3">
        <div v-if="isGroup" class="relative size-[22px] shrink-0">
          <div
            v-for="(copyright, index) in infringement.copyrights.slice(0, 3)"
            :key="copyright.id"
            class="absolute inset-0"
            :style="getStackedStyles(index)"
          >
            <Thumbnail
              :type="fileTypeToThumbnail(copyright.type)"
              :src="previewImageUrl(copyright.asset?.previewImageUrl)"
            />
          </div>
        </div>

        <div v-else class="size-[22px] shrink-0">
          <Thumbnail
            :type="fileTypeToThumbnail(infringement.copyrights[0].type)"
            :src="
              previewImageUrl(infringement.copyrights[0].asset?.previewImageUrl)
            "
          />
        </div>

        <div v-truncate class="w-full text-[13px] leading-[18px] text-gray-900">
          <div v-if="isGroup" data-truncate-target>
            {{ formatNumber(infringement.copyrights.length) }} copyrights
          </div>
          <div v-else data-truncate-target>
            {{ infringement.copyrights[0].name }}
          </div>
        </div>
      </div>

      <div class="flex items-center gap-x-3">
        <div class="size-[22px] shrink-0">
          <Thumbnail
            :type="fileTypeToThumbnail(infringement.infringingFile.type)"
            :src="
              infringement.infringingFile.asset.previewImageUrl || undefined
            "
          />
        </div>

        <div v-truncate class="w-full text-[13px] leading-[18px] text-gray-900">
          <div data-truncate-target>
            {{ infringement.infringingFile.name }}
          </div>
        </div>
      </div>
    </div>
  </component>
</template>

<script setup lang="ts">
import { isAcceptedCoverMatch } from '@/features/infringement/utils/acceptedCover'
import { computed } from 'vue'
import { RouterLink } from 'vue-router'
import { Pill, Progress, Thumbnail } from '@dacr/library'
import {
  ChevronRightIcon,
  FileIssueIcon,
  FlagIcon,
  MenuIcon,
  SortIcon,
} from '@dacr/icons'
import {
  InfringementLabel,
  ReportStatus,
  ScoreLevel,
  type InfringementListQuery,
} from '@/shared/graphql/router/graphql'
import { fileTypeToThumbnail } from '@/shared/utils/thumbnail'
import { openSheet, Sheets } from '@/shared/dialogs/sheets'
import { openDialog, Dialog } from '@/shared/dialogs/dialog'
import { formatNumber } from '@/shared/utils/number'
import { applyAuthorizationToken } from '@/shared/utils/url'
import CreatingReport from './CreatingReport.vue'

type InfringementNode =
  InfringementListQuery['infringements']['edges'][number]['node']

const props = defineProps<{
  infringement: InfringementNode
}>()

const isGroup = computed(() => props.infringement.copyrights.length > 1)

const isReportLabel = computed(
  () => props.infringement.label === InfringementLabel.Report
)

const isReport = computed(() => !isGroup.value && isReportLabel.value)

const isProcessing = computed(
  () =>
    !isGroup.value &&
    props.infringement.report?.status === ReportStatus.Processing
)

const isFlagged = computed(() => !isGroup.value && !isReportLabel.value)

const coverOnly = computed(
  () =>
    isAcceptedCoverMatch(props.infringement) &&
    (props.infringement.matches?.score ?? 0) === 0
)
const score = computed(() => props.infringement.matches?.score ?? 0)
const previewImageUrl = (url?: string | null) => applyAuthorizationToken(url)

const levelLabel = computed(() => {
  switch (props.infringement.matches?.level) {
    case ScoreLevel.Low:
      return 'Minimal match'
    case ScoreLevel.Medium:
      return 'Moderate match'
    case ScoreLevel.High:
      return 'Significant match'
    default:
      return 'Not assessed'
  }
})

const accentText = computed(() => ({
  'text-gray-300': props.infringement.matches?.level === ScoreLevel.Low,
  'text-yellow-500': props.infringement.matches?.level === ScoreLevel.Medium,
  'text-red-500': props.infringement.matches?.level === ScoreLevel.High,
}))

const accentBg = computed(() => ({
  'bg-gray-100 text-gray-800': !props.infringement.matches,
  'text-white': !!props.infringement.matches,
  'bg-gray-300': props.infringement.matches?.level === ScoreLevel.Low,
  'bg-yellow-500': props.infringement.matches?.level === ScoreLevel.Medium,
  'bg-red-500': props.infringement.matches?.level === ScoreLevel.High,
}))

const levelColor = computed(() => {
  switch (props.infringement.matches?.level) {
    case ScoreLevel.Low:
      return 'var(--color-gray-300)'
    case ScoreLevel.Medium:
      return 'var(--color-yellow-500)'
    case ScoreLevel.High:
      return 'var(--color-red-500)'
    default:
      return 'var(--color-gray-300)'
  }
})

const labelIcon = computed(() =>
  isReportLabel.value ? FileIssueIcon : FlagIcon
)

const rowLink = computed(() =>
  isGroup.value
    ? `/infringement/group/${props.infringement.infringingFile.id}/${new Date(props.infringement.timestamp).getTime()}`
    : `/infringement/report/${props.infringement.report?.id}`
)

const getStackedStyles = (index: number) => {
  const count = Math.min(props.infringement.copyrights.length, 3)
  const rotate = index === count - 1 ? 0 : index === 0 ? -16 : 16
  return { transform: `rotate(${rotate}deg)` }
}

const handleMenu = () => {
  openSheet(Sheets.InfringementMenu, {
    props: { infringement: props.infringement },
  })
}

const handleRowClick = () => {
  if (!isFlagged.value) return
  openDialog(Dialog.FlaggedMatch, {
    props: { infringement: props.infringement },
  })
}
</script>
